import { AchievementsModel } from "@/features/achievements/achievementsModel";
import { achievementDefinitions } from "@/features/achievements/achievementDefinitions";
import { computePersistentEffects } from "@/features/achievements/achievementEffects";
import { normalizeAchievementId } from "@/features/achievements/achievementIdAliases";
import type {
  AchievementId,
  AchievementsState,
} from "@/features/achievements/types";
import { HoverIdleModel } from "@/features/hoverIdle/core/hoverIdleModel";
import type { HovSkillState } from "@/features/hoverIdle/hov/hovSkillModel";
import { HovSkillModel } from "@/features/hoverIdle/hov/hovSkillModel";
import { isHovSkillId } from "@/features/hoverIdle/hov/hovSkillDefinitions";
import type { TimSkillState } from "@/features/hoverIdle/tim/timSkillModel";
import { TimSkillModel } from "@/features/hoverIdle/tim/timSkillModel";
import { isTimSkillId } from "@/features/hoverIdle/tim/timSkillDefinitions";
import type { HoverIdleModelState } from "@/features/hoverIdle/types";
import {
  compareHuge,
  huge,
  hugeZero,
  maxHuge,
  type HugeNumber,
} from "@/shared/numbers";

export type ParseAdminResourceLog10InputResult =
  | { readonly ok: true; readonly value: HugeNumber }
  | { readonly ok: false; readonly reason: "invalid" | "negative" };

export function adminResourceLog10ToHuge(log10: number): HugeNumber {
  return huge(10).pow(log10);
}

export function parseAdminResourceLog10Input(
  input: string,
): ParseAdminResourceLog10InputResult {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: "invalid" };
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return { ok: false, reason: "invalid" };
  }
  if (parsed < 0) {
    return { ok: false, reason: "negative" };
  }
  return { ok: true, value: adminResourceLog10ToHuge(parsed) };
}

export interface HoverIdleAdminStateBundle {
  readonly modelState: HoverIdleModelState;
  readonly hovSkillState: HovSkillState;
  readonly timSkillState: TimSkillState;
  readonly achievementsState: AchievementsState;
  readonly applyOptions?: HoverIdleAdminStateApplyOptions;
}

export interface HoverIdleAdminStateApplyOptions {
  readonly hovCounterAdjustment?: "preserve-requested-current-loop";
}

export type HoverIdleAdminStateBundleResult =
  | {
      readonly ok: true;
      readonly data: HoverIdleAdminStateBundle;
    }
  | {
      readonly ok: false;
      readonly reason: string;
    };

const VALID_ACHIEVEMENT_IDS = new Set<AchievementId>(
  achievementDefinitions.map((definition) => definition.id),
);

/**
 * Adjust the bundle's earned-Hov counters so that admin Hov increases also
 * bump cumulative + current-loop counters, while admin Hov decreases never
 * reduce them.
 *
 * - admin set hov > current hov: counters grow by at least the delta
 * - admin set hov <= current hov: counters never shrink below previous
 * - progress-builder fresh-run bundles may preserve the requested current-loop
 *   counter while still protecting cumulative total Hov
 */
export function adjustAdminBundleForCumulativeHov(
  bundle: HoverIdleAdminStateBundle,
  previous: {
    readonly hov: HugeNumber;
    readonly totalHovEarned: HugeNumber;
    readonly currentLoopHovEarned: HugeNumber;
  },
): HoverIdleAdminStateBundle {
  const newHov = bundle.modelState.economy.hov;
  const delta =
    compareHuge(newHov, previous.hov) > 0
      ? newHov.sub(previous.hov)
      : hugeZero();
  const requestedTotal = bundle.modelState.cumulativeStats.totalHovEarned;
  const adjustedTotal = maxHuge(
    requestedTotal,
    previous.totalHovEarned.add(delta),
  );
  const requestedLoop = bundle.modelState.economy.mHov.currentLoopHovEarned;
  const adjustedLoop =
    bundle.applyOptions?.hovCounterAdjustment ===
    "preserve-requested-current-loop"
      ? requestedLoop
      : maxHuge(requestedLoop, previous.currentLoopHovEarned.add(delta));
  if (
    compareHuge(adjustedTotal, requestedTotal) === 0 &&
    compareHuge(adjustedLoop, requestedLoop) === 0
  ) {
    return bundle;
  }
  return {
    ...bundle,
    modelState: {
      ...bundle.modelState,
      economy: {
        ...bundle.modelState.economy,
        mHov: {
          ...bundle.modelState.economy.mHov,
          currentLoopHovEarned: adjustedLoop,
        },
      },
      cumulativeStats: {
        ...bundle.modelState.cumulativeStats,
        totalHovEarned: adjustedTotal,
      },
    },
  };
}

export function serializeHoverIdleAdminStateBundle(
  bundle: HoverIdleAdminStateBundle,
): string {
  return JSON.stringify(bundle, null, 2);
}

export function deserializeHoverIdleAdminStateBundle(
  serialized: string,
  options: { readonly nowMs?: () => number } = {},
): HoverIdleAdminStateBundleResult {
  try {
    return validateHoverIdleAdminStateBundle(
      JSON.parse(serialized) as unknown,
      options,
    );
  } catch (error) {
    return fail(error instanceof Error ? error.message : "invalid JSON");
  }
}

export function validateHoverIdleAdminStateBundle(
  value: unknown,
  options: { readonly nowMs?: () => number } = {},
): HoverIdleAdminStateBundleResult {
  if (!isRecord(value)) {
    return fail("admin state must be an object");
  }

  const modelState = readRecord(value, "modelState");
  if (typeof modelState === "string") return fail(modelState);

  const hovSkillState = readRecord(value, "hovSkillState");
  if (typeof hovSkillState === "string") return fail(hovSkillState);

  const timSkillState = readRecord(value, "timSkillState");
  if (typeof timSkillState === "string") return fail(timSkillState);

  const achievementsState = readRecord(value, "achievementsState");
  if (typeof achievementsState === "string") return fail(achievementsState);

  const applyOptions = readAdminStateApplyOptions(value.applyOptions);
  if (typeof applyOptions === "string") return fail(applyOptions);

  const validations: readonly (string | null)[] = [
    requireSkillIdArray(
      hovSkillState,
      "acquiredIds",
      "hovSkillState.acquiredIds",
      isHovSkillId,
    ),
    requireSkillIdArray(
      timSkillState,
      "acquiredIds",
      "timSkillState.acquiredIds",
      isTimSkillId,
    ),
    requireSkillIdArray(
      achievementsState,
      "unlockedIds",
      "achievementsState.unlockedIds",
      isAchievementId,
    ),
    requireSkillIdArray(
      achievementsState,
      "seenIds",
      "achievementsState.seenIds",
      isAchievementId,
    ),
  ];

  const failure = validations.find((result) => result !== null);
  if (failure !== undefined && failure !== null) {
    return fail(failure);
  }

  try {
    const achievementsModel = new AchievementsModel();
    achievementsModel.restoreState(
      achievementsState as unknown as AchievementsState,
    );

    const model = new HoverIdleModel(undefined, options);
    const achievementEffects = computePersistentEffects(
      achievementsModel.exportState().unlockedIds,
    );
    model.setAchievementPersistentEffects(achievementEffects);
    model.restoreState(modelState as unknown as HoverIdleModelState);
    model.setAchievementPersistentEffects(achievementEffects);
    const normalizedModelState = model.exportState();

    const hovSkillModel = new HovSkillModel();
    hovSkillModel.restoreState(hovSkillState as unknown as HovSkillState);

    const timSkillModel = new TimSkillModel();
    timSkillModel.restoreState(timSkillState as unknown as TimSkillState);

    return {
      ok: true,
      data: {
        modelState: normalizedModelState,
        hovSkillState: hovSkillModel.exportState(),
        timSkillState: timSkillModel.exportState(),
        achievementsState: achievementsModel.exportState(),
        ...(applyOptions === undefined ? {} : { applyOptions }),
      },
    };
  } catch (error) {
    return fail(reasonFromUnknown(error));
  }
}

function readAdminStateApplyOptions(
  value: unknown,
): HoverIdleAdminStateApplyOptions | string | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    return "applyOptions must be an object";
  }
  const hovCounterAdjustment = value.hovCounterAdjustment;
  if (hovCounterAdjustment === undefined) return {};
  if (hovCounterAdjustment !== "preserve-requested-current-loop") {
    return 'applyOptions.hovCounterAdjustment must be "preserve-requested-current-loop"';
  }
  return { hovCounterAdjustment };
}

function fail(reason: string): HoverIdleAdminStateBundleResult {
  return { ok: false, reason };
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRecord(
  record: Readonly<Record<string, unknown>>,
  key: string,
): Readonly<Record<string, unknown>> | string {
  const value = record[key];
  if (!isRecord(value)) {
    return `${key} must be an object`;
  }

  return value;
}

function requireStringArray(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
): string | null {
  const value = record[key];
  if (!Array.isArray(value)) {
    return `${path} must be an array`;
  }

  for (const item of value) {
    if (typeof item !== "string") {
      return `${path} must contain only strings`;
    }
  }

  return null;
}

function requireSkillIdArray<T extends string>(
  record: Readonly<Record<string, unknown>>,
  key: string,
  path: string,
  predicate: (value: string) => value is T,
): string | null {
  const arrayValidation = requireStringArray(record, key, path);
  if (arrayValidation !== null) {
    return arrayValidation;
  }

  const value = record[key] as readonly string[];
  for (const item of value) {
    if (!predicate(item)) {
      return `${path} contains an unknown id: ${item}`;
    }
  }

  return null;
}

function isAchievementId(value: string): value is AchievementId {
  const id = normalizeAchievementId(value);
  return id !== null && VALID_ACHIEVEMENT_IDS.has(id);
}
